/**
 * Anexos no chat do assistente (rodadas 2 e 3 do bloco "anexos e voz",
 * ADR-0015).
 *
 * Tipos aceitos: IMAGEM (png/jpeg/webp — redimensionada em canvas aqui, para
 * cortar custo do modelo de visao) e DOCUMENTO (PDF e Word .docx — enviados
 * CRUS, porque encolher esses formatos exigiria interpreta-los, trabalho que
 * o servidor ja faz de forma controlada em document-extraction.ts). Por isso
 * os tetos de tamanho sao por TIPO: 5MB imagem, 10MB documento, 12MB
 * agregados por mensagem.
 *
 * API de anexo usada: `experimental_attachments` — Array<{name?, contentType?,
 * url}> passado para `handleSubmit(event, { experimental_attachments })` e
 * espelhado em `Message.experimental_attachments`. Esta e a API do AI SDK 4
 * (pacote `@ai-sdk/react` na faixa ^1.x, que e o instalado neste projeto —
 * ver apps/web/package.json). O AI SDK 5 (`@ai-sdk/react` ^2.x) troca isso
 * por `files`/`parts` com `FileUIPart` e `sendMessage({ files })`; NAO se
 * aplica aqui. Confirmado lendo o .d.ts REALMENTE instalado (nao a doc):
 * node_modules/@ai-sdk/ui-utils/dist/index.d.ts — `interface Attachment`,
 * `Message.experimental_attachments`, `ChatRequestOptions.experimental_attachments`;
 * e a implementacao de `handleSubmit` em node_modules/@ai-sdk/react/dist/index.js
 * (aceita `Array<Attachment>` direto, sem reprocessar, quando nao e um FileList).
 */

export const MAX_ATTACHMENTS_PER_MESSAGE = 3;

/**
 * Teto do arquivo de IMAGEM original selecionado, antes de redimensionar.
 * Documentos tem teto proprio (maior) — ver MAX_DOCUMENT_SOURCE_BYTES.
 */
export const MAX_ATTACHMENT_SOURCE_BYTES = 5 * 1024 * 1024;

/**
 * Teto por arquivo de DOCUMENTO (PDF/.docx). Maior que o de imagem porque
 * documento vai CRU para a api: o resize em canvas so se aplica a imagem, nao
 * ha como encolher um PDF no client. Espelha MAX_DOCUMENT_ATTACHMENT_BYTES no
 * servidor (apps/api/.../document-extraction.ts).
 */
export const MAX_DOCUMENT_SOURCE_BYTES = 10 * 1024 * 1024;

/**
 * Teto AGREGADO dos anexos de uma mensagem. Espelha
 * MAX_ATTACHMENTS_TOTAL_BYTES no servidor, que existe por causa do bodyLimit
 * do Fastify (25MB; base64 infla ~4/3). Validar aqui tambem faz o usuario
 * receber uma mensagem clara em vez de um 413 cru no envio.
 */
export const MAX_ATTACHMENTS_TOTAL_BYTES = 12 * 1024 * 1024;

export const ALLOWED_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;

/**
 * Documentos aceitos. `.doc` binario (application/msword) NAO entra: e outro
 * formato, que a lib de leitura do servidor (mammoth) nao suporta.
 */
export const ALLOWED_DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
] as const;

export const ALLOWED_ATTACHMENT_MIME_TYPES = [
  ...ALLOWED_IMAGE_MIME_TYPES,
  ...ALLOWED_DOCUMENT_MIME_TYPES,
] as const;
export type AllowedAttachmentMimeType = (typeof ALLOWED_ATTACHMENT_MIME_TYPES)[number];

/**
 * accept= do <input type="file">. Alem dos mimetypes, inclui as EXTENSOES:
 * alguns sistemas/navegadores nao associam o mimetype longo do .docx no
 * dialogo de arquivo, e sem a extensao o arquivo apareceria como nao
 * selecionavel.
 */
export const ATTACHMENT_INPUT_ACCEPT = [
  ...ALLOWED_ATTACHMENT_MIME_TYPES,
  '.pdf',
  '.docx',
].join(',');

/** Lado maior apos redimensionar (px). Recorte de custo/latencia do modelo de vision. */
export const ATTACHMENT_MAX_DIMENSION = 1568;

/** Qualidade de reencode (canvas.toBlob) para webp/jpeg. */
export const ATTACHMENT_ENCODE_QUALITY = 0.85;

export type AttachmentKind = 'image' | 'document';

export interface PendingAttachment {
  id: string;
  name: string;
  contentType: string;
  /**
   * Imagem: data URL JA redimensionado (serve de preview e de envio).
   * Documento: data URL do arquivo CRU (nao ha preview de miniatura — a UI
   * mostra icone + nome; ver attachment-thumbnails.tsx).
   */
  dataUrl: string;
  /** Tamanho em bytes do que sera enviado (imagem: ja redimensionada). */
  byteSize: number;
  kind: AttachmentKind;
}

export function isAllowedAttachmentType(type: string): type is AllowedAttachmentMimeType {
  return (ALLOWED_ATTACHMENT_MIME_TYPES as readonly string[]).includes(type);
}

/**
 * Descobre o tipo do arquivo. Usa `file.type` quando o browser preencheu e,
 * como fallback, a EXTENSAO — o Windows as vezes entrega `file.type` vazio
 * para .docx dependendo do que esta registrado no sistema, e nesse caso o
 * anexo seria recusado sem motivo real.
 */
export function resolveAttachmentMimeType(file: File): string {
  if (file.type && isAllowedAttachmentType(file.type)) return file.type;

  const lower = file.name.toLowerCase();
  if (lower.endsWith('.pdf')) return 'application/pdf';
  if (lower.endsWith('.docx')) {
    return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  }
  return file.type;
}

/** Classifica o anexo; null quando o tipo nao e aceito. */
export function attachmentKind(mimeType: string): AttachmentKind | null {
  if ((ALLOWED_IMAGE_MIME_TYPES as readonly string[]).includes(mimeType)) return 'image';
  if ((ALLOWED_DOCUMENT_MIME_TYPES as readonly string[]).includes(mimeType)) return 'document';
  return null;
}

/** Teto de bytes do arquivo ORIGINAL conforme o tipo. */
export function maxSourceBytesForKind(kind: AttachmentKind): number {
  return kind === 'image' ? MAX_ATTACHMENT_SOURCE_BYTES : MAX_DOCUMENT_SOURCE_BYTES;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Calcula as dimensoes de destino mantendo a proporcao, reduzindo somente se
 * o lado maior exceder `maxSide` — nunca aumenta uma imagem menor que o alvo.
 * Pura (sem canvas/DOM) para ser testavel isoladamente.
 */
export function computeResizedDimensions(
  width: number,
  height: number,
  maxSide: number = ATTACHMENT_MAX_DIMENSION,
): { width: number; height: number } {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return { width: maxSide, height: maxSide };
  }
  const largest = Math.max(width, height);
  if (largest <= maxSide) {
    return { width: Math.round(width), height: Math.round(height) };
  }
  const scale = maxSide / largest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

export interface AttachmentValidationResult {
  /** Arquivos aceitos para processar, respeitando o teto de quantidade. */
  accepted: File[];
  /** Mensagens de erro em PT-BR, uma por arquivo rejeitado. */
  errors: string[];
}

/**
 * Valida uma selecao de arquivos ANTES de processar: tipo permitido, tamanho
 * do arquivo ORIGINAL dentro do teto DO TIPO (imagem 5MB, documento 10MB),
 * teto de MAX_ATTACHMENTS_PER_MESSAGE e teto AGREGADO de bytes — todos
 * contando o que ja esta anexado nesta mensagem.
 *
 * Aceita PARCIALMENTE: se o usuario selecionar mais arquivos do que cabe,
 * aceita os primeiros que cabem e reporta erro claro so para os excedentes —
 * em vez de descartar a selecao inteira por causa de 1 arquivo invalido.
 *
 * `existingBytes` e o total ja acumulado nos anexos pendentes; sem ele o teto
 * agregado poderia ser furado anexando um arquivo grande de cada vez.
 */
export function validateAttachmentSelection(
  files: File[],
  existingCount: number,
  existingBytes = 0,
): AttachmentValidationResult {
  const accepted: File[] = [];
  const errors: string[] = [];
  let remaining = MAX_ATTACHMENTS_PER_MESSAGE - existingCount;
  let totalBytes = existingBytes;

  for (const file of files) {
    if (remaining <= 0) {
      errors.push(
        `Máximo de ${MAX_ATTACHMENTS_PER_MESSAGE} arquivos por mensagem. "${file.name}" não foi anexado.`,
      );
      continue;
    }

    const mimeType = resolveAttachmentMimeType(file);
    const kind = attachmentKind(mimeType);
    if (!kind) {
      errors.push(
        `"${file.name}" não é um tipo suportado (use imagem PNG/JPEG/WEBP, PDF ou Word .docx).`,
      );
      continue;
    }

    const maxBytes = maxSourceBytesForKind(kind);
    if (file.size > maxBytes) {
      errors.push(
        `"${file.name}" tem ${formatBytes(file.size)}, acima do limite de ${formatBytes(maxBytes)} para ${
          kind === 'image' ? 'imagem' : 'documento'
        }.`,
      );
      continue;
    }

    if (totalBytes + file.size > MAX_ATTACHMENTS_TOTAL_BYTES) {
      errors.push(
        `"${file.name}" não foi anexado: os arquivos da mensagem somariam mais de ${formatBytes(
          MAX_ATTACHMENTS_TOTAL_BYTES,
        )}.`,
      );
      continue;
    }

    accepted.push(file);
    totalBytes += file.size;
    remaining -= 1;
  }

  return { accepted, errors };
}

/** Troca a extensao do nome do arquivo para bater com o contentType final (webp/jpeg). */
export function renameForContentType(originalName: string, contentType: string): string {
  const ext = contentType === 'image/jpeg' ? 'jpg' : 'webp';
  const base = originalName.replace(/\.[^./\\]+$/, '').trim();
  return `${base || 'imagem'}.${ext}`;
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Não foi possível processar a imagem.'));
    reader.readAsDataURL(blob);
  });
}

interface ImageSource {
  draw: CanvasImageSource;
  width: number;
  height: number;
  cleanup: () => void;
}

/** Carrega o arquivo como fonte desenhavel em canvas, com fallback entre APIs do browser. */
async function loadImageSource(file: File): Promise<ImageSource> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file);
      return {
        draw: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        cleanup: () => bitmap.close(),
      };
    } catch {
      // createImageBitmap pode falhar para certos formatos/browsers — cai no
      // fallback via <img> abaixo em vez de propagar o erro.
    }
  }

  const objectUrl = URL.createObjectURL(file);
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error('Não foi possível ler a imagem.'));
    el.src = objectUrl;
  });
  return {
    draw: img,
    width: img.naturalWidth,
    height: img.naturalHeight,
    cleanup: () => URL.revokeObjectURL(objectUrl),
  };
}

/**
 * Redimensiona e reencode uma imagem no client (canvas) ANTES de enviar:
 * lado maior no maximo ATTACHMENT_MAX_DIMENSION, reencode em webp (fallback
 * automatico para jpeg se o browser nao suportar encode de webp) na
 * qualidade ATTACHMENT_ENCODE_QUALITY.
 *
 * Isto corta custo de token e latencia do modelo de vision drasticamente —
 * nunca mandamos o arquivo cru da camera (que pode vir com 4000px+ de lado
 * e vários MB). Depende de Canvas/Image/FileReader (APIs de browser); nao e
 * testavel em ambiente `node` do Vitest deste projeto (ver vitest.config.ts)
 * — a logica pura ao redor (computeResizedDimensions, validateAttachmentSelection)
 * e testada isoladamente; esta funcao e validada manualmente no browser.
 */
export async function resizeImageFile(file: File): Promise<PendingAttachment> {
  const source = await loadImageSource(file);
  try {
    const { width, height } = computeResizedDimensions(source.width, source.height);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Não foi possível processar a imagem.');
    ctx.drawImage(source.draw, 0, 0, width, height);

    let blob = await canvasToBlob(canvas, 'image/webp', ATTACHMENT_ENCODE_QUALITY);
    let contentType = 'image/webp';
    if (!blob || blob.type !== 'image/webp') {
      // Browser sem suporte a encode de webp via canvas (raro hoje): jpeg e
      // universalmente suportado e ainda assim reduz bastante o tamanho.
      blob = await canvasToBlob(canvas, 'image/jpeg', ATTACHMENT_ENCODE_QUALITY);
      contentType = 'image/jpeg';
    }
    if (!blob) throw new Error('Não foi possível processar a imagem.');

    const dataUrl = await blobToDataUrl(blob);
    return {
      id: crypto.randomUUID(),
      name: renameForContentType(file.name, contentType),
      contentType,
      dataUrl,
      byteSize: blob.size,
      kind: 'image',
    };
  } finally {
    source.cleanup();
  }
}

/**
 * Le um DOCUMENTO (PDF/.docx) como data URL, sem qualquer transformacao.
 *
 * Nao ha equivalente ao resize aqui de proposito: encolher um PDF ou um .docx
 * no browser exigiria interpretar o formato (e, no caso do PDF escaneado,
 * rasterizar pagina por pagina) — trabalho que o SERVIDOR ja faz de forma
 * controlada em document-extraction.ts. O client so valida e transporta; o
 * teto de 10MB por documento e o agregado de 12MB seguram o custo.
 */
export async function readDocumentFile(
  file: File,
  mimeType: string,
): Promise<PendingAttachment> {
  const dataUrl = await blobToDataUrl(file);
  return {
    id: crypto.randomUUID(),
    name: file.name,
    contentType: mimeType,
    dataUrl,
    byteSize: file.size,
    kind: 'document',
  };
}

/**
 * Prepara um arquivo JA validado para envio, despachando pelo tipo: imagem
 * passa pelo resize em canvas; documento vai cru. Ponto unico de entrada
 * usado pelo widget, para ele nao precisar saber a diferenca.
 */
export async function prepareAttachment(file: File): Promise<PendingAttachment> {
  const mimeType = resolveAttachmentMimeType(file);
  const kind = attachmentKind(mimeType);
  if (kind === 'document') return readDocumentFile(file, mimeType);
  return resizeImageFile(file);
}
